import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Users, Building, TrendingUp, Compass, Handshake, Calculator } from 'lucide-react';
import { ParallaxShapes } from '../components/ParallaxShapes';
import { useSEO } from '../hooks/useSEO';

// SVG Drawing Component that animates based on scroll
const ScrollDrawing = ({ scrollProgress }) => {
  const dashOffset = 1000 - (scrollProgress * 1000);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0 opacity-[0.07]">
      <svg 
        viewBox="0 0 1920 1080" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid slice"
      >
        <g stroke="#264F77" fill="none" strokeWidth="2">
          <path 
            d="M400,800 L400,300 L600,200 L800,300 L800,800"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
          <path 
            d="M450,400 L550,400 M450,500 L550,500 M450,600 L550,600 M650,400 L750,400 M650,500 L750,500 M650,600 L750,600"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset * 0.8}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
          <path 
            d="M900,800 L900,250 L1000,250 L1000,200 L1100,200 L1100,250 L1200,250 L1200,800"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset * 0.9}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
          <path 
            d="M1300,800 L1300,150 L1500,150 L1500,800"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset * 0.85}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
          <path 
            d="M1600,800 L1600,100 L1800,100 M1600,100 L1600,50 M1650,100 L1650,150"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset * 0.7}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
          <path 
            d="M100,800 L1900,800"
            strokeDasharray="1000"
            strokeDashoffset={dashOffset * 0.95}
            style={{ transition: 'stroke-dashoffset 0.1s ease-out' }}
          />
        </g>
      </svg>
    </div>
  );
};

// Parallax Image Component
const ParallaxImage = ({ src, alt, className, scrollY, speed = 0.1, direction = 'up' }) => {
  const translateY = direction === 'up' ? -scrollY * speed : scrollY * speed;
  
  return (
    <div className={`overflow-hidden rounded-2xl shadow-2xl ${className}`}>
      <div 
        className="w-full h-full transform transition-transform duration-700 hover:scale-105"
        style={{ transform: `translateY(${translateY}px)` }}
      >
        <img 
          src={src} 
          alt={alt}
          title={alt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export const Strategy = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(window.scrollY / totalHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      icon: Building,
      title: 'Immobilienentwicklungen und Projektrealisierungen',
      intro: 'Wir entwickeln nachhaltig und wertsteigernd, mit Fokus auf den Schnittpunkt zwischen Nutzer- und Investorenbedürfnissen sowie nachhaltigem Lebensraum.',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/yp8kv7el_Baustelle%20LIVAG%20WebP.webp',
      sections: [
        {
          subtitle: 'Unterstützung durch unsere Entwicklungsprofis',
          text: 'Unser erfahrenes Entwicklungsteam unterstützt Sie bei jeder Phase der Projektentwicklung – von der Standortanalyse über die Planung bis zur Vermarktung. Mit umfassendem Know-how und kreativen Lösungen sorgen wir für eine reibungslose und erfolgreiche Realisierung Ihrer Immobilienprojekte.'
        },
        {
          subtitle: 'Kreative Ansätze für nachhaltige Erträge',
          text: 'Unsere kreativen Ansätze garantieren stabile Mieterträge und reduzieren Verwaltungskosten. Durch intelligente Planung und nachhaltige Bauweise schaffen wir Immobilien, die langfristig rentabel und effizient sind.'
        }
      ]
    },
    {
      icon: TrendingUp,
      title: 'Optimierung von Immobilienbeständen',
      intro: 'In jedem Immobilienbestand schlummern Potenziale, die es zu heben gilt. Wir identifizieren verborgene Qualitäten und passen bestehende Strukturen an moderne Anforderungen an.',
      image: 'https://customer-assets.emergentagent.com/job_76278b7b-dd8f-4002-9c8d-eddff31203c4/artifacts/oo75ucr9_Bild%20weiss%20Immobilienentwicklung.webp',
      sections: [
        {
          subtitle: 'Sanierung und Wertsteigerung',
          text: 'Durch innovative Konzepte und nachhaltige Bauweisen schaffen wir zukunftsweisende, kosteneffiziente Lösungen, die den Wert Ihrer Immobilie maximieren und deren Marktattraktivität steigern.'
        },
        {
          subtitle: 'Fundierte Beratung und Entscheidungsgrundlage',
          text: 'Dank unserer langjährigen Erfahrung und dem kontinuierlichen Austausch mit Bauträgern bieten wir fundierte Einschätzungen und Strategien, die Ihnen eine solide Basis für Ihre Investitionsentscheidungen geben.'
        }
      ]
    },
    {
      icon: Handshake,
      title: 'Immobilienvermittlung, die Mehrwert schafft',
      intro: 'Als Immobilienmakler gestalten wir weit mehr als nur Transaktionen – wir erschaffen Lebensräume, die perfekt auf Ihre individuellen Wünsche und Bedürfnisse zugeschnitten sind.',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/hnmuyc8t_Plan%20Helm.webp',
      sections: [
        {
          subtitle: 'Ihr Partner für das passende Zuhause',
          text: 'Mit uns finden Sie nicht nur Ihr ideales Zuhause, sondern haben auch die Möglichkeit, den Wert Ihrer Immobilieninvestitionen oder Ihres gesamten Portfolios gezielt zu steigern. Durch unsere umfassende Marktkenntnis und unser starkes Netzwerk bieten wir Ihnen massgeschneiderte Lösungen.'
        },
        {
          subtitle: 'Individuelle Beratung und aktives Management',
          text: 'Wir nehmen uns Zeit, um Ihr Suchprofil genau zu verstehen und bieten Ihnen eine aktive, proaktive Beratung. Unser Team analysiert Ihre Anforderungen und Bedürfnisse detailliert, um Ihnen gezielt passende Immobilienangebote zu präsentieren.'
        }
      ]
    },
    {
      icon: Calculator,
      title: 'Finanzierungsberatungen auf den Punkt',
      intro: 'Wir wissen, dass der Kauf einer Immobilie eine bedeutende finanzielle Entscheidung ist. Jede Transaktion erfordert eine sorgfältige Abwägung der Möglichkeiten und eine fundierte Entscheidung.',
      image: 'https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/4u0odyon_Planung.webp',
      sections: [
        {
          subtitle: 'Ganzheitliche Beratung',
          text: 'Wir legen grossen Wert darauf, unsere Mandanten umfassend zu beraten und bei jedem Schritt des Prozesses zu unterstützen, damit Sie sich sicher und gut informiert fühlen.'
        },
        {
          subtitle: 'Neutrale Lagebeurteilung und massgeschneiderte Finanzierung',
          text: 'Unsere Spezialisierung liegt in der ganzheitlichen Betreuung, die sowohl eine neutrale Beurteilung der Lage und Marktchancen als auch die Vermittlung passender Finanzierungslösungen umfasst. Mit unserer Erfahrung und unserem Netzwerk bieten wir Ihnen ein Rundum-Paket für den erfolgreichen Immobilienkauf.'
        }
      ]
    }
  ];

  const values = [
    {
      icon: Leaf,
      title: 'Effizienter Umgang mit unseren Ressourcen',
      text: 'In unserer Baupraxis legen wir besonderen Wert auf nachhaltige Ressourcennutzung. Durch die Implementierung integrierter Abwicklungsmodelle schaffen wir die Grundlage für die erfolgreiche Einführung einer Kreislaufwirtschaft. Die digitale Bauwerksdokumentation ermöglicht eine umfassende Transparenz bezüglich Daten und Materialien.'
    },
    {
      icon: Users,
      title: 'Diversifikation',
      text: 'Mit dem Zuwachs an Nutzungs- und soziokultureller Vielfalt geht eine verstärkte Diversifikation einher. Nachhaltige Bauweise und Flächennutzung, durchdachte Grundrisse, kurze Wege und vielfältig durchmischte Quartiere stehen im Fokus. Durch gezielte Diversifikation und Förderung von Biodiversität schaffen wir lebendige Wohn-, Arbeits- und Freizeitmöglichkeiten.'
    },
    {
      icon: Compass,
      title: 'Mehr Wohnraum durch Innenverdichtung',
      text: 'Innenverdichtung ist ökologisch und bietet Qualität für alle Mieter oder Eigentümer, wenn soziale, architektonische und umweltpolitische Aspekte im Einklang stehen. So schaffen wir Wohnraum, wo es ihn wirklich braucht, reduzieren den Mobilitätsbedarf und die CO₂-Emissionen und schonen unsere wertvollen Landressourcen.'
    }
  ];

  useSEO('strategy');

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100" ref={containerRef}>
      {/* Parallax Shapes Background */}
      <ParallaxShapes scrollY={scrollY} />
      
      {/* Animated SVG Drawing Background */}
      <ScrollDrawing scrollProgress={scrollProgress} />
      
      {/* Content */}
      <div className="relative" style={{ zIndex: 2 }}>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-[#264F77] text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{ transform: `translateY(${scrollY * 0.2}px)` }}
          >
            <div className="absolute top-20 left-10 w-64 h-64 border border-white/30 rounded-full" />
            <div className="absolute top-40 right-20 w-32 h-32 border border-white/20 rotate-45" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Unsere Strategie
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                Wir entwickeln nachhaltig und wertsteigernd, mit Fokus auf den Schnittpunkt zwischen Nutzer- und Investorenbedürfnissen sowie nachhaltigem Lebensraum.
              </p>
            </div>
          </div>
        </section>

        {/* Services Sections with Images */}
        {services.map((service, index) => {
          const Icon = service.icon;
          const isEven = index % 2 === 0;
          
          return (
            <section 
              key={index}
              className={`py-20 relative overflow-hidden ${isEven ? 'bg-white' : 'bg-gray-50'}`}
            >
              {/* Parallax decorative element */}
              <div 
                className={`absolute ${isEven ? 'right-0' : 'left-0'} top-1/2 -translate-y-1/2 w-96 h-96 bg-[#264F77]/5 rounded-full blur-3xl`}
                style={{ transform: `translate(${isEven ? scrollY * 0.02 : -scrollY * 0.02}px, -50%)` }}
              />
              
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center`}>
                  {/* Image Side */}
                  <div className="lg:w-1/2">
                    <div 
                      className="relative group"
                      style={{ 
                        transform: `translateY(${Math.sin(scrollY * 0.005 + index) * 20}px)`,
                        transition: 'transform 0.5s ease-out'
                      }}
                    >
                      {/* Main Image */}
                      <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                        <img 
                          src={service.image} 
                          alt={`${service.title} - LIVAG Strategie für nachhaltige Immobilienentwicklung Ostschweiz`}
                          title={`${service.title} | Wie Haus kaufen oder verkaufen mit LIVAG`}
                          loading="lazy"
                          className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        {/* Gradient Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#264F77]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                      
                      {/* Decorative Frame */}
                      <div 
                        className={`absolute -z-10 ${isEven ? '-right-4 -bottom-4' : '-left-4 -bottom-4'} w-full h-full border-2 border-[#264F77]/20 rounded-2xl`}
                        style={{ 
                          transform: `translate(${isEven ? scrollY * 0.01 : -scrollY * 0.01}px, ${scrollY * 0.01}px)`,
                          transition: 'transform 0.3s ease-out'
                        }}
                      />
                      
                      {/* Floating Icon Badge */}
                      <div 
                        className={`absolute ${isEven ? '-right-6 top-8' : '-left-6 top-8'} w-16 h-16 bg-[#264F77] rounded-xl flex items-center justify-center shadow-xl`}
                        style={{ 
                          transform: `translateY(${Math.cos(scrollY * 0.01 + index) * 10}px)`,
                          transition: 'transform 0.3s ease-out'
                        }}
                      >
                        <Icon className="text-white" size={28} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Side */}
                  <div className="lg:w-1/2">
                    <h2 className="text-3xl md:text-4xl font-normal text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-700 leading-relaxed mb-8">
                      {service.intro}
                    </p>
                    
                    <div className="space-y-6">
                      {service.sections.map((section, sIndex) => (
                        <div 
                          key={sIndex} 
                          className="border-l-4 border-[#264F77] pl-6 hover:border-l-8 transition-all duration-300"
                        >
                          <h3 className="text-xl font-semibold text-[#264F77] mb-2">
                            {section.subtitle}
                          </h3>
                          <p className="text-gray-600 leading-relaxed">
                            {section.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* Values Section */}
        <section className="py-24 bg-[#264F77] text-white relative overflow-hidden">
          <div 
            className="absolute inset-0 opacity-10"
            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
          >
            <div className="absolute top-20 right-1/4 w-60 h-60 border border-white/30 rounded-full" />
            <div className="absolute bottom-20 left-1/4 w-40 h-40 border border-white/20 rotate-45" />
          </div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Ihre Perspektiven durch unsere gelebten Werte
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Nachhaltigkeit, Innovation und Qualität bilden das Fundament unserer Arbeit.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all duration-500 group"
                    style={{ 
                      transform: `translateY(${Math.sin(scrollY * 0.008 + index * 2) * 15}px)`,
                      transition: 'transform 0.3s ease-out'
                    }}
                  >
                    <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-white/30 transition-colors duration-300">
                      <Icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-2xl font-normal mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {value.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {value.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-normal text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Lassen Sie uns gemeinsam Zukunft gestalten
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Entdecken Sie, wie wir Ihre Immobilienziele verwirklichen können.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-colors duration-300"
            >
              Kostenloses Erstgespräch vereinbaren
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </section>
      </div>
      
    </div>
  );
};
